<script setup lang="ts">
  import logger from '@leaflink/snitch';
  import uniqueId from 'lodash-es/uniqueId';
  import { computed, useAttrs, useCssModule, useSlots } from 'vue';

  export interface CheckboxProps {
    /**
     * Whether the checkbox is currently checked based on the current value.
     * Either defined by a boolean, or whether `checked` as an array includes `value`.
     */
    checked: boolean | Array<string | number>;

    /**
     * Whether the checkbox is in an error state (optional). For error styling without error text.
     *
     * @deprecated Just pass `error-text` instead.
     */
    hasError?: boolean;

    /**
     * Unique ID for the checkbox, required for accessibility purposes
     */
    id?: string;

    /**
     * Label text to display with checkbox
     */
    label?: string;

    /**
     * Error text to display. Replaces `hintText` (if provided) and adds error styling (overrides `hasError`).
     */
    errorText?: string;

    /**
     * Displays text below the input; hidden when the isReadOnly prop is truthy.
     */
    hintText?: string;

    /**
     * @deprecated use the `checked` prop instead
     */
    modelValue?: boolean | Array<string | number>;

    /**
     * The value of the checkbox.
     */
    value?: string | number;

    /**
     * Whether the checkbox is disabled.
     */
    disabled?: boolean;
  }

  defineOptions({
    name: 'll-checkbox',
    inheritAttrs: false,
  });

  const props = withDefaults(defineProps<CheckboxProps>(), {
    errorId: undefined,
    errorText: '',
    hasError: false,
    hintText: '',
    id: undefined,
    label: undefined,
    modelValue: undefined,
    value: undefined,
    disabled: false,
  });

  const attrs = useAttrs();
  const slots = useSlots();
  const classes = useCssModule();

  const emit = defineEmits<{
    (e: 'update:checked', value: CheckboxProps['checked']): void;
  }>();

  if (props.modelValue !== undefined) {
    throw new Error('ll-checkbox: use :checked or v-model:checked instead of :model-value and v-model.');
  }

  if (typeof props.value === 'boolean') {
    logger.warn('ll-checkbox: use `checked` to toggle the state; `value` is what will be set if `checked` is true');
  }

  if (attrs.onChange) {
    throw new Error('ll-checkbox: use the @update:checked event instead of @change.');
  }

  const inputAttrs = computed(() => {
    const tempAttrs = { ...attrs };

    delete tempAttrs['data-test'];
    delete tempAttrs.class;

    return tempAttrs;
  });

  const internalValue = computed({
    get() {
      return props.checked;
    },
    set(value) {
      emit('update:checked', value);
    },
  });

  const internalHasError = computed(() => !!props.errorText || props.hasError);
  const checkboxId = computed(() => props.id || uniqueId('checkbox-'));
  const checkboxErrorId = computed(() => (props.errorText ? uniqueId('checkbox-error-') : undefined));
</script>

<template>
  <div
    class="stash-checkbox relative m-0"
    :class="[
      attrs.class,
      {
        'stash-checkbox--checked': !!internalValue,
        [`stash-checkbox--has-error ${classes['has-error']}`]: internalHasError,
      },
    ]"
    :data-test="attrs['data-test'] || 'stash-checkbox'"
  >
    <input
      :id="checkboxId"
      v-model="internalValue"
      :aria-invalid="!!props.errorText"
      :aria-errormessage="checkboxErrorId"
      :disabled="props.disabled"
      class="sr-only lg:m-0"
      :class="classes.input"
      type="checkbox"
      :value="value"
      v-bind="inputAttrs"
    />

    <label
      data-test="stash-checkbox|label"
      class="stash-checkbox__label relative inline-block min-h-9 cursor-pointer overflow-visible py-1.5 pl-8 align-top text-sm font-normal leading-6"
      :class="[
        classes.label,
        {
          'text-ice-900': !internalHasError && !props.disabled,
          'text-red-500': internalHasError,
          'text-ice-500': props.disabled,
        },
      ]"
      :for="checkboxId"
    >
      <slot>
        {{ label }}
      </slot>
    </label>

    <span
      v-if="props.errorText"
      :id="checkboxErrorId"
      class="stash-checkbox__error-text ml-8 block whitespace-pre-line text-xs text-red-500"
      data-test="stash-checkbox|error"
    >
      {{ props.errorText }}
    </span>
    <span
      v-else-if="props.hintText || slots.hint"
      class="stash-checkbox__hint-text ml-8 block whitespace-pre-line text-xs"
      :class="{ 'text-ice-500': props.disabled }"
      data-test="stash-checkbox|hint"
    >
      <!-- @slot for displaying hint text -->
      <slot name="hint">{{ props.hintText }}</slot>
    </span>
  </div>
</template>

<style module>
  @reference "../../../styles/main.css";

  @layer utilities {
    .input:disabled + .label {
      cursor: auto;
    }

    .label::before {
      content: '';
      border: 1px solid var(--color-ice-500);
      display: inline-block;
      vertical-align: top;
    }

    .label::before,
    .label::after {
      border-radius: var(--radius-sm);
      height: 20px;
      left: 0;
      position: absolute;
      top: --spacing(1.5);
      width: 20px;
    }

    .has-error .label::before,
    .has-error .input {
      border-color: var(--color-red-500);
    }

    .input:indeterminate + .label::after {
      content: url('data:image/svg+xml; utf8, <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M6 10.4C6 10.1791 6.17909 10 6.4 10H17.6C17.8209 10 18 10.1791 18 10.4V12.6C18 12.8209 17.8209 13 17.6 13H6.4C6.17909 13 6 12.8209 6 12.6V10.4Z" fill="white"/></svg>');
    }

    .input:checked + .label::after {
      content: url('data:image/svg+xml; utf8, <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M17.89 8.281a.723.723 0 0 1 .22.531.723.723 0 0 1-.22.532l-6.718 6.719a.723.723 0 0 1-.531.218.723.723 0 0 1-.532-.218l-3.89-3.891A.723.723 0 0 1 6 11.64c0-.209.073-.386.219-.532l1.062-1.062a.723.723 0 0 1 .532-.219.72.72 0 0 1 .53.219l2.298 2.305 5.125-5.133a.723.723 0 0 1 .53-.219c.21 0 .386.073.532.219l1.063 1.062z" fill="white"/></svg>');
      background: var(--color-blue-500);
      height: 20px;
      width: 20px;
      top: --spacing(1.5);
    }

    .input:checked + .label::before,
    .input:indeterminate + .label::before {
      background: var(--color-blue-500);
      border-color: var(--color-blue-500);
    }

    .input:disabled + .label::before,
    .input:disabled + .label::after {
      background: var(--color-ice-100);
      border: 1px solid var(--color-ice-500);
    }

    .input:disabled:is(:checked, :indeterminate) + .label::after {
      background: var(--color-ice-500);
    }

    .input:not(:disabled, :checked, :indeterminate) + .label:hover::before {
      border-color: var(--color-blue-500);
    }
  }
</style>
